import { URL_QUERY } from './failureDetail';

export const CAPGO_PREFIX_IOS = '🔴 ✨  CapgoUpdater : ';
export const CAPGO_PREFIX_ANDROID = '[CapgoUpdater] 🔴 ';

const CAPGO_PREFIXES = [CAPGO_PREFIX_IOS, CAPGO_PREFIX_ANDROID];
const TARGET_VERSION = /app_versions(?:%2F|\/)(?:com\.baobab\.app_)?(\d+\.\d+\.\d+)/i;

export const stripCapgoPrefix = (line: string): string | null => {
  const prefix = CAPGO_PREFIXES.find((candidate) => line.startsWith(candidate));
  return prefix === undefined ? null : line.slice(prefix.length);
};

export const stripUrlQueries = (text: string): string => text.replace(URL_QUERY, '$1');

export const extractTargetVersion = (text: string): string | undefined =>
  TARGET_VERSION.exec(text)?.[1];

export type CapgoReasonClass =
  | 'disk_precheck' | 'disk_full' | 'fs_permission' | 'fs_rename' | 'fs_mkdir'
  | 'unzip' | 'checksum' | 'decode'
  | 'timeout_wallclock' | 'timeout_idle' | 'dns' | 'offline' | 'conn_lost' | 'conn_failed' | 'tls' | 'bad_url'
  | 'http_4xx' | 'http_5xx' | 'android_no_reason' | 'unclassified';

export interface CapgoReason {
  reasonClass: CapgoReasonClass;
  httpStatus?: number;
  reasonRaw: string;
}

const MAX_INPUT = 1000;
const MAX_REASON_RAW = 200;
const HTTP_URL = /https?:\/\/\S+/gi;

// Order is the classification priority: a disk or fs failure outranks the network phrase that often follows it.
const PHRASE_CLASSES: [CapgoReasonClass, RegExp][] = [
  ['disk_precheck', /insufficient disk space|insufficient_disk_space/i],
  ['disk_full', /no space left|enospc|plus d'espace|non c'è più spazio|não (há|resta) espaço|nicht genügend (freier )?speicher/i],
  ['fs_permission', /operation not permitted|opération non permise/i],
  ['fs_rename', /failed to rename temp file/i],
  ['fs_mkdir', /failed to create destination directory/i],
  ['unzip', /cannot be unzip|unzip/i],
  ['checksum', /checksum/i],
  ['decode', /brotli|decompress|decoding failed/i],
  ['timeout_wallclock', /timed out downloading|timed out after \d+s/i],
  ['timeout_idle', /request timed out|requête a expiré|solicitação expirou|richiesta è scaduta|zeitüberschreitung|sockettimeout|(^|: )timeout$/i],
  ['dns', /unable to resolve host|nom d'hôte|hostname could not be found|nome (d[eo] )?host|angegebenen hostnamen/i],
  ['offline', /semble interrompue|appears to be offline|parece estar desativada|sembra non essere attiva|scheint nicht zu bestehen/i],
  ['conn_lost', /a été perdue|foi perdida|è stata persa|wurde unterbrochen|connection was lost|connection abort|connection reset|unexpected end of stream/i],
  ['conn_failed', /failed to connect to|connexion au serveur impossible|could not connect to the server|impossibile connettersi al server|no se (?:pudo|ha podido) conectar (?:con el|al) servidor|keine verbindung zum server hergestellt|não foi possível (?:conectar(?:-se)?|ligar|estabelecer ligação) (?:ao|com o) servidor/i],
  ['tls', /\btls\b|\bssl\b|secure connection|sichere verbindung/i],
  ['bad_url', /ungültige url|url incorrecte|unsupported url|url non valida|invalid url/i],
];
const HTTP_STATUS = [/http error: \(?(\d{3})\b/i, /status code \(?(\d{3})\b/i, /http \(?(\d{3})\b/i];
const ANDROID_NO_REASON = /^Failed to download from: \S+$|Download failed with status:/i;

const LEADING_URL_LABEL = /^Failed to download from:\s*\S*\s*(?:-\s+)?/i;
const LEADING_FILE_LABEL = /^Error processing file:\s*\S+\s+Error in downloadAndVerify:\s*/i;
const TOKEN_PARAM = /token=\S*/gi;
const IPV4 = /\b\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?\b/g;
const IPV6 = /\[?(?:[0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}\]?(?::\d+)?/gi;
const FILE_TOKEN = /\S+\.(?:js|css|html|json|br|map|woff2?|png|svg|webp)\b/gi;
const OBJECT_HASH = /@[0-9a-f]+\b/gi;
const HEX_RUN = /[0-9a-f]{8,}/gi;
const DIGIT_RUN = /\d{3,}/g;

const normalise = (text: string): string => {
  const body = stripCapgoPrefix(text) ?? text;
  return body.trim().normalize('NFC').replace(/[‘’]/g, "'").slice(0, MAX_INPUT);
};

const sanitiseReason = (text: string): string =>
  text
    .replace(LEADING_URL_LABEL, '')
    .replace(LEADING_FILE_LABEL, '')
    .replace(HTTP_URL, '')
    .replace(TOKEN_PARAM, '')
    .replace(IPV4, '')
    .replace(IPV6, '')
    .replace(FILE_TOKEN, '')
    .replace(OBJECT_HASH, '')
    .replace(HEX_RUN, '')
    .replace(DIGIT_RUN, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_REASON_RAW)
    .normalize('NFC');

const httpStatusOf = (text: string): number | undefined => {
  for (const pattern of HTTP_STATUS) {
    const status = Number(pattern.exec(text)?.[1]);
    if (status >= 400 && status <= 599) return status;
  }
  return undefined;
};

export const classifyCapgoReason = (text: string): CapgoReason => {
  const normalised = normalise(text);
  const reasonRaw = sanitiseReason(normalised);
  const phrases = normalised.replace(HTTP_URL, '');
  const phraseClass = PHRASE_CLASSES.find(([, pattern]) => pattern.test(phrases));
  if (phraseClass !== undefined) return { reasonClass: phraseClass[0], reasonRaw };
  const httpStatus = httpStatusOf(phrases);
  if (httpStatus !== undefined) {
    return { reasonClass: httpStatus >= 500 ? 'http_5xx' : 'http_4xx', httpStatus, reasonRaw };
  }
  if (ANDROID_NO_REASON.test(normalised)) return { reasonClass: 'android_no_reason', reasonRaw };
  return { reasonClass: 'unclassified', reasonRaw };
};

export const isContentReason = (c: CapgoReasonClass): boolean =>
  c === 'checksum' || c === 'unzip' || c === 'decode' || c === 'http_4xx';

export const isDiskReason = (c: CapgoReasonClass): boolean =>
  c === 'disk_precheck' || c === 'disk_full';

export const isLowNetworkReason = (c: CapgoReasonClass): boolean =>
  c === 'timeout_wallclock' || c === 'timeout_idle' || c === 'conn_lost';
